import type { Participant } from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { showModal } from '../../components/generic/Modal';
import { useParticipantEmbeddableFormsQuery } from '../../graphql/hooks/useParticipantQuery';
import { usePermission } from '../../hooks/usePermission';
import { ParticipantEmbeddableFormCreateModal } from './ParticipantEmbeddableFormCreateModal';
import { ParticipantEmbeddableFormListItem } from './ParticipantEmbeddableFormListItem';

interface Props {
  participant: Participant;
  isDisabled?: boolean;
}

export const ParticipantEmbeddableFormCard = (props: Props) => {
  const { participant } = props;

  const createParticipantEmbeddableFormPermission = usePermission(
    'create',
    'participantEmbeddableForm',
  );
  const isDisabled = props.isDisabled || !createParticipantEmbeddableFormPermission;

  const query = useParticipantEmbeddableFormsQuery({
    organizationId: participant.organizationId,
    participantId: participant.id,
  });

  const participantEmbeddableForms = query.data?.organization?.participant?.embeddableForms ?? [];

  const content = P.run(() => {
    if (query.isLoading) {
      return <Spin />;
    }

    if (P.isEmpty(participantEmbeddableForms)) {
      return <Empty h='full' title='No embeddable forms found' p={0} />;
    }

    return (
      <ChakraV3.Stack gap={6}>
        {participantEmbeddableForms.map((form) => (
          <ParticipantEmbeddableFormListItem key={form.id} form={form} participant={participant} />
        ))}
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Card.Root h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Heading size='xl' fontWeight='bold'>
            Forms
          </ChakraV3.Heading>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Manage this participant's embeddable forms
          </ChakraV3.Text>
        </ChakraV3.Stack>
      </ChakraV3.Card.Header>
      <ChakraV3.Card.Body>
        <ChakraV3.Stack gap={6}>
          {content}
          <ChakraV3.Button
            onClick={() =>
              showModal(ParticipantEmbeddableFormCreateModal, {
                participantId: participant.id,
              })
            }
            disabled={isDisabled}
            size='sm'
            variant='subtle'
          >
            <FaPlus />
            Add embeddable form
          </ChakraV3.Button>
        </ChakraV3.Stack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
